/**
 * Feedback components (UX-002 §8 screen states, UX-004 §4). The rule that drives
 * the design: an empty screen must say WHICH kind of empty it is — verified-quiet
 * (a positive statement) and no-data are distinct, never the same blank panel.
 */
import type { Figure } from "@solarkey/api-client";
import type { ReactNode } from "react";

import { reasonText } from "../grammar";

/* ── Banner ─────────────────────────────────────────────────────────────────
   A scope-level statement: data path down, sync state. One statement, not many
   alarms. */
export function Banner(props: {
  tone?: "info" | "path_down" | "sync" | "warning";
  children: ReactNode;
  onDismiss?: () => void;
}): ReactNode {
  return (
    <div className="sk-banner" data-tone={props.tone ?? "info"} role="status">
      <span className="sk-banner__body">{props.children}</span>
      {props.onDismiss ? (
        <button
          type="button"
          className="sk-banner__dismiss"
          aria-label="Dismiss"
          onClick={props.onDismiss}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}

/* ── EmptyState ─────────────────────────────────────────────────────────────
   Verified-quiet (nothing needs attention AND the data path is healthy — stated
   in words) vs no-data (nothing reporting / configured, with the last-known
   time). Distinct variants, never interchangeable (UX-003 principle 5). */
export function EmptyState(props: {
  variant: "verified_quiet" | "no_data" | "withheld";
  title: string;
  message?: string;
  /** For the `withheld` variant: the figure's reason. It rides as a TOOLTIP, kept
   *  but voiced quietly — never a paragraph on the glass, never an internal ref. */
  reason?: Figure["reason"];
  lastKnownAt?: string;
}): ReactNode {
  // UX-REV-001 U1: a panel with nothing to show at this altitude carries ONE muted
  // line, not a paragraph explaining its own absence. The honesty is kept — the
  // reason is still there, as a hover tooltip — but it is not spent on hero space.
  // "Collapse" and "do not render at this altitude" are the screen's call; this is
  // the one-muted-line treatment the screen reaches for when it does show something.
  if (props.variant === "withheld") {
    const tip = reasonText(props.reason) ?? props.message ?? undefined;
    return (
      <div className="sk-empty sk-empty--quiet" role="note" title={tip}>
        <span className="sk-empty__mark" aria-hidden="true">—</span>
        <span className="sk-empty__title">{props.title}</span>
      </div>
    );
  }
  const quiet = props.variant === "verified_quiet";
  return (
    <div className="sk-empty" data-variant={props.variant} role="note">
      <div className="sk-empty__mark" aria-hidden="true">
        {quiet ? "✓" : "—"}
      </div>
      <div className="sk-empty__title">{props.title}</div>
      {props.message ? <div className="sk-empty__message">{props.message}</div> : null}
      {!quiet && props.lastKnownAt ? (
        <div className="sk-empty__lastknown">Last known: {props.lastKnownAt}</div>
      ) : null}
    </div>
  );
}

/* ── Skeleton ───────────────────────────────────────────────────────────────
   Skeletons in the shape of the content, never a spinner (UX-002 §8). */
export function Skeleton(props: {
  variant?: "text" | "tile" | "row";
  lines?: number;
  width?: number | string;
}): ReactNode {
  const variant = props.variant ?? "text";
  if (variant === "text") {
    const lines = props.lines ?? 3;
    return (
      <div className="sk-skeleton sk-skeleton--text" aria-hidden="true">
        {Array.from({ length: lines }).map((_, i) => (
          <span
            key={i}
            className="sk-skeleton__line"
            style={{ width: i === lines - 1 ? "60%" : "100%" }}
          />
        ))}
      </div>
    );
  }
  return (
    <div
      className={`sk-skeleton sk-skeleton--${variant}`}
      style={{ width: props.width }}
      aria-hidden="true"
    />
  );
}

/* ── ErrorState ─────────────────────────────────────────────────────────────
   What failed, while the last-good data stays visible with its age (UX-002 §8). */
export function ErrorState(props: {
  title?: string;
  message?: string;
  lastGoodAt?: string;
  onRetry?: () => void;
}): ReactNode {
  return (
    <div className="sk-errorstate" role="alert">
      <div className="sk-errorstate__title">{props.title ?? "Couldn’t load this"}</div>
      {props.message ? <div className="sk-errorstate__message">{props.message}</div> : null}
      {props.lastGoodAt ? (
        <div className="sk-errorstate__lastgood">
          Showing last good data from {props.lastGoodAt}
        </div>
      ) : null}
      {props.onRetry ? (
        <button type="button" className="sk-errorstate__retry" onClick={props.onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

/* ── Toast ──────────────────────────────────────────────────────────────────
   A transient notice — never for anything that matters (UX-003 §7). Important
   things link to records; they are not only a toast. */
export function Toast(props: {
  message: string;
  tone?: "info" | "success" | "warning";
  onDismiss?: () => void;
}): ReactNode {
  return (
    <div className="sk-toast" data-tone={props.tone ?? "info"} role="status">
      <span className="sk-toast__message">{props.message}</span>
      {props.onDismiss ? (
        <button
          type="button"
          className="sk-toast__dismiss"
          aria-label="Dismiss"
          onClick={props.onDismiss}
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}
